import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Users, Clock, CalendarDays, TrendingUp, Download, BarChart3, PieChart as PieChartIcon, IndianRupee, Briefcase, MapPin, CheckCircle, XCircle, CalendarRange, Filter } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#0D2850','#059669','#b45309','#7c3aed','#dc2626','#0284c7','#be185d','#4f46e5','#d97706'];
const TOOLTIP_STYLE = { contentStyle: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, padding: '8px 12px' } };
const INR = v => `\u20B9${Number(v || 0).toLocaleString('en-IN')}`;
const fmtDate = d => d ? d.toISOString().slice(0, 10) : null;

const PRESETS = [
  { label: 'Last 7d', days: 7 },
  { label: 'Last 30d', days: 30 },
  { label: 'Last 3m', days: 90 },
  { label: 'This Year', days: -1 },
  { label: 'All Time', days: 0 },
];

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [expData, setExpData] = useState(null);
  const [payData, setPayData] = useState(null);
  const [jrnData, setJrnData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [compMetric, setCompMetric] = useState('attendance');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [activePreset, setActivePreset] = useState('All Time');
  const [calOpen, setCalOpen] = useState(false);

  const dateParams = useMemo(() => {
    const p = {};
    if (dateRange.from) p.start_date = fmtDate(dateRange.from);
    if (dateRange.to) p.end_date = fmtDate(dateRange.to);
    return p;
  }, [dateRange]);

  const qs = useMemo(() => {
    const parts = [];
    if (dateParams.start_date) parts.push(`start_date=${dateParams.start_date}`);
    if (dateParams.end_date) parts.push(`end_date=${dateParams.end_date}`);
    return parts.length ? `?${parts.join('&')}` : '';
  }, [dateParams]);

  const fetchData = useCallback(async (qStr) => {
    setLoading(true);
    try {
      const [r, er, pr, jr, hm, cm] = await Promise.all([
        api.get(`/api/reports/analytics${qStr}`),
        api.get(`/api/reports/expense-analytics${qStr}`).catch(() => ({ data: null })),
        api.get(`/api/reports/payroll-analytics${qStr}`).catch(() => ({ data: null })),
        api.get(`/api/reports/journey-analytics${qStr}`).catch(() => ({ data: null })),
        api.get(`/api/reports/attendance-heatmap${qStr ? qStr : '?months=3'}`).catch(() => ({ data: null })),
        api.get(`/api/reports/comparison?metric=${compMetric}${qStr ? '&' + qStr.slice(1) : ''}`).catch(() => ({ data: null })),
      ]);
      setData(r.data); setExpData(er.data); setPayData(pr.data); setJrnData(jr.data);
      setHeatmapData(hm.data); setComparisonData(cm.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [compMetric]);

  useEffect(() => { fetchData(qs); }, [qs, fetchData]);

  useEffect(() => {
    api.get('/api/reports/scheduled').then(r => setScheduledReports(r.data)).catch(() => {});
  }, []);

  const applyPreset = (preset) => {
    setActivePreset(preset.label);
    if (preset.days === 0) {
      setDateRange({ from: null, to: null });
    } else if (preset.days === -1) {
      const now = new Date();
      setDateRange({ from: new Date(now.getFullYear(), 0, 1), to: now });
    } else {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - preset.days);
      setDateRange({ from, to: now });
    }
  };

  const onCalSelect = (range) => {
    if (range?.from) {
      setDateRange({ from: range.from, to: range.to || range.from });
      setActivePreset('');
      if (range.to) setCalOpen(false);
    }
  };

  const clearDates = () => {
    setDateRange({ from: null, to: null });
    setActivePreset('All Time');
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      const dateQs = qs ? '&' + qs.slice(1) : '';
      const endpoint = format === 'pdf'
        ? `/api/reports/export-pdf?report_type=${type}${dateQs}`
        : `/api/reports/export?report_type=${type}${dateQs}`;
      const r = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${type}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} ${format.toUpperCase()} exported`);
    } catch { toast.error('Export failed'); }
  };

  const handleAttendanceDetailExport = async (format = 'csv') => {
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append('start_date', fmtDate(dateRange.from));
      if (dateRange.to) params.append('end_date', fmtDate(dateRange.to));
      params.append('format', format);
      const toastId = toast.loading(`Generating ${format.toUpperCase()}…`);
      const r = await api.get(`/api/reports/attendance-detail?${params.toString()}`, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_detail.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Attendance details ${format.toUpperCase()} downloaded`, { id: toastId });
    } catch (e) {
      const msg = e?.response?.status
        ? `Export failed (HTTP ${e.response.status})`
        : (e?.message || 'Detailed attendance export failed');
      toast.error(msg);
    }
  };

  const [activeTab, setActiveTab] = useState('workforce');
  const [scheduledReports, setScheduledReports] = useState([]);
  const [triggerLoading, setTriggerLoading] = useState(false);

  const switchCompMetric = async (metric) => {
    setCompMetric(metric);
    try {
      const r = await api.get(`/api/reports/comparison?metric=${metric}${qs ? '&' + qs.slice(1) : ''}`);
      setComparisonData(r.data);
    } catch { /* ignore */ }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-12" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div>;
  if (!data) return <p className="text-center text-slate-400 py-12">Failed to load analytics</p>;

  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// REPORTS</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Reports & Analytics</h1>
          <p className="text-sm text-slate-500">Organization-wide insights and metrics</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {activeTab === 'workforce' && (
            <>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('headcount')} data-testid="export-headcount"><Download className="w-3 h-3" />CSV</Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleExport('workforce', 'pdf')} data-testid="export-workforce-pdf"><Download className="w-3 h-3" />PDF</Button>
            </>
          )}
          {activeTab === 'attendance' && (
            <>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('attendance')} data-testid="export-attendance"><Download className="w-3 h-3" />Summary CSV</Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleExport('attendance', 'pdf')} data-testid="export-attendance-pdf"><Download className="w-3 h-3" />Summary PDF</Button>
              <Button variant="outline" size="sm" className="gap-1 border-[#0D2850] text-[#0D2850] hover:bg-[#0D2850]/5" onClick={() => handleAttendanceDetailExport('csv')} data-testid="export-attendance-detail-csv"><Download className="w-3 h-3" />Detailed CSV</Button>
              <Button size="sm" className="gap-1 bg-[#0D2850] hover:bg-[#0B2246]" onClick={() => handleAttendanceDetailExport('pdf')} data-testid="export-attendance-detail-pdf"><Download className="w-3 h-3" />Detailed PDF</Button>
            </>
          )}
          {activeTab === 'leave' && (
            <>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('leave')} data-testid="export-leave"><Download className="w-3 h-3" />CSV</Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleExport('leave', 'pdf')} data-testid="export-leave-pdf"><Download className="w-3 h-3" />PDF</Button>
            </>
          )}
          {activeTab === 'payroll' && (
            <>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('payroll_summary')} data-testid="export-payroll"><Download className="w-3 h-3" />CSV</Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleExport('payroll_summary', 'pdf')} data-testid="export-payroll-pdf"><Download className="w-3 h-3" />PDF</Button>
            </>
          )}
          {activeTab === 'expenses' && (
            <>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('expenses')} data-testid="export-expenses"><Download className="w-3 h-3" />CSV</Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleExport('expenses', 'pdf')} data-testid="export-expenses-pdf"><Download className="w-3 h-3" />PDF</Button>
            </>
          )}
          {activeTab === 'journey' && (
            <>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('journey')} data-testid="export-journey"><Download className="w-3 h-3" />CSV</Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleExport('journey', 'pdf')} data-testid="export-journey-pdf"><Download className="w-3 h-3" />PDF</Button>
            </>
          )}
        </div>
      </div>

      {/* Date Range Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="date-range-filter">
        <CalendarRange className="w-4 h-4 text-slate-400 shrink-0" />
        {PRESETS.map(p => (
          <Button key={p.label} variant={activePreset === p.label ? 'default' : 'ghost'} size="sm"
            className={`text-xs h-7 px-2.5 ${activePreset === p.label ? 'bg-[#0D2850] hover:bg-[#0D2850]/90' : 'text-slate-600'}`}
            onClick={() => applyPreset(p)} data-testid={`preset-${p.label.replace(/\s/g, '-').toLowerCase()}`}>
            {p.label}
          </Button>
        ))}
        <div className="h-5 w-px bg-slate-200 mx-1" />
        <Popover open={calOpen} onOpenChange={setCalOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="text-xs h-7 gap-1.5" data-testid="custom-date-picker">
              <Filter className="w-3 h-3" />
              {dateRange.from && dateRange.to
                ? `${fmtDate(dateRange.from)} — ${fmtDate(dateRange.to)}`
                : 'Custom Range'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="range" selected={dateRange} onSelect={onCalSelect}
              numberOfMonths={2} defaultMonth={dateRange.from || new Date()}
            />
          </PopoverContent>
        </Popover>
        {(dateRange.from || dateRange.to) && (
          <Button variant="ghost" size="sm" className="text-xs h-7 text-red-500 hover:text-red-700" onClick={clearDates} data-testid="clear-dates">
            Clear
          </Button>
        )}
        {loading && <span className="text-[10px] text-slate-400 ml-auto animate-pulse">Updating...</span>}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricCard icon={Users} label="Total Employees" value={s.total_employees} sub={`${s.departments} departments`} color="text-[#0D2850]" />
        <MetricCard icon={Clock} label="Attendance Rate" value={`${s.attendance_rate}%`} sub={`${s.total_att_records} records`} color="text-emerald-600" />
        <MetricCard icon={TrendingUp} label="Late Arrivals" value={`${s.late_percentage}%`} sub="of total attendance" color="text-amber-600" />
        <MetricCard icon={CalendarDays} label="Leave Utilization" value={`${s.leave_utilization}%`} sub="of entitled leaves used" color="text-violet-600" />
        <MetricCard icon={BarChart3} label="Payroll Runs" value={data.payroll_runs?.length || 0} sub="periods processed" color="text-blue-600" />
      </div>

      <Tabs defaultValue="workforce" onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="workforce">Workforce</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leave">Leave</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="journey" data-testid="tab-journey">Journey Plans</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">Scheduled Reports</TabsTrigger>
        </TabsList>

        {/* Workforce Tab */}
        <TabsContent value="workforce" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Headcount by Department</CardTitle></CardHeader>
              <CardContent className="p-3">
                <div className="h-[260px]" data-testid="headcount-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.dept_headcount} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip {...TOOLTIP_STYLE} />
                      <Bar dataKey="count" name="Employees" fill="#0D2850" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Location Distribution</CardTitle></CardHeader>
              <CardContent className="p-3">
                <div className="h-[260px]" data-testid="location-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.location_distribution} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="count" nameKey="location" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} style={{ fontSize: 11 }}>
                        {data.location_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip {...TOOLTIP_STYLE} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Department Table */}
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Department Summary</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Department</TableHead><TableHead className="text-center">Headcount</TableHead><TableHead className="text-right">% of Org</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.dept_headcount.map((d, i) => (
                    <TableRow key={d.department}>
                      <TableCell className="font-medium text-sm flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />{d.department}</TableCell>
                      <TableCell className="text-center text-sm">{d.count}</TableCell>
                      <TableCell className="text-right text-sm">{(d.count / s.total_employees * 100).toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="mt-4 space-y-4">
          {data.attendance_daily.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Daily Attendance Trend (Last 30 days)</CardTitle></CardHeader>
              <CardContent className="p-3 pr-5">
                <div className="h-[260px]" data-testid="attendance-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.attendance_daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d?.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip {...TOOLTIP_STYLE} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="present" name="Present" stroke="#059669" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="late" name="Late" stroke="#b45309" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200">
            <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Attendance Rate by Department</CardTitle></CardHeader>
            <CardContent className="p-3">
              <div className="h-[250px]" data-testid="att-dept-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.attendance_by_dept} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                    <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={v => `${v}%`} {...TOOLTIP_STYLE} />
                    <Bar dataKey="rate" name="Attendance Rate" fill="#059669" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leave Tab */}
        <TabsContent value="leave" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Leave Type Breakdown (Org-wide)</CardTitle></CardHeader>
              <CardContent className="p-3">
                <div className="h-[240px]" data-testid="leave-type-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.leave_type_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip {...TOOLTIP_STYLE} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="entitled" name="Entitled" fill="#0D2850" radius={[4,4,0,0]} />
                      <Bar dataKey="used" name="Used" fill="#dc2626" radius={[4,4,0,0]} />
                      <Bar dataKey="balance" name="Balance" fill="#059669" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Leave Utilization by Department</CardTitle></CardHeader>
              <CardContent className="p-3">
                <div className="h-[240px]" data-testid="leave-dept-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.leave_utilization_by_dept} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                      <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={v => `${v}%`} {...TOOLTIP_STYLE} />
                      <Bar dataKey="utilization" name="Utilization %" fill="#7c3aed" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Leave Balance Table */}
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Department-wise Leave Details</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Department</TableHead><TableHead className="text-center">Entitled</TableHead><TableHead className="text-center">Used</TableHead><TableHead className="text-center">Balance</TableHead><TableHead className="text-right">Utilization</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.leave_utilization_by_dept.map(d => (
                    <TableRow key={d.department}>
                      <TableCell className="font-medium text-sm">{d.department}</TableCell>
                      <TableCell className="text-center text-sm">{d.entitled}</TableCell>
                      <TableCell className="text-center text-sm">{d.used}</TableCell>
                      <TableCell className="text-center text-sm font-semibold">{d.entitled - d.used}</TableCell>
                      <TableCell className="text-right">
                        <Badge className={`text-[10px] ${d.utilization > 75 ? 'bg-red-100 text-red-700 hover:bg-red-100' : d.utilization > 50 ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}`}>
                          {d.utilization}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Analytics Tab */}
        <TabsContent value="payroll" className="mt-4 space-y-4" data-testid="payroll-analytics-tab">
          {payData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard icon={IndianRupee} label="Latest Gross Payroll" value={INR(payData.summary.latest_gross)} sub={payData.summary.latest_period || 'No runs'} color="text-[#0D2850]" />
                <MetricCard icon={IndianRupee} label="Latest Net Payroll" value={INR(payData.summary.latest_net)} sub={`${payData.summary.latest_employees} employees`} color="text-emerald-600" />
                <MetricCard icon={TrendingUp} label="Avg Gross Salary" value={INR(payData.summary.avg_gross_salary)} sub="per employee" color="text-violet-600" />
                <MetricCard icon={BarChart3} label="Payroll Runs" value={payData.summary.payroll_runs} sub="periods processed" color="text-blue-600" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {payData.monthly_trend?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Monthly Payroll Trend</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[260px]" data-testid="payroll-trend-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={payData.monthly_trend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="period" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={50} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip {...TOOLTIP_STYLE} formatter={v => INR(v)} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="gross" name="Gross" fill="#0D2850" radius={[4,4,0,0]} />
                            <Bar dataKey="net" name="Net" fill="#059669" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {payData.by_department?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Department-wise Payroll Distribution</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[260px]" data-testid="payroll-dept-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={payData.by_department} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="gross" nameKey="department" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} style={{ fontSize: 10 }}>
                              {payData.by_department.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip {...TOOLTIP_STYLE} formatter={v => INR(v)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {payData.salary_bands?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Salary Band Distribution (Monthly Gross)</CardTitle></CardHeader>
                  <CardContent className="p-3">
                    <div className="h-[220px]" data-testid="salary-band-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={payData.salary_bands}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="band" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip {...TOOLTIP_STYLE} />
                          <Bar dataKey="count" name="Employees" fill="#7c3aed" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {payData.top_earners?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Top Earners (Latest Run)</CardTitle></CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50"><TableHead>#</TableHead><TableHead>Employee</TableHead><TableHead>Department</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {payData.top_earners.map((s, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm font-medium text-slate-400">{i + 1}</TableCell>
                            <TableCell className="text-sm font-medium">{s.employee_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{s.department}</TableCell>
                            <TableCell className="text-right text-sm">{INR(s.gross)}</TableCell>
                            <TableCell className="text-right text-sm font-semibold text-emerald-700">{INR(s.net)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-slate-200"><CardContent className="p-12 text-center"><p className="text-sm text-slate-400">No payroll data available</p></CardContent></Card>
          )}
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="mt-4 space-y-4">
          {expData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard icon={IndianRupee} label="Total Claimed" value={`\u20B9${(expData.summary.total_amount || 0).toLocaleString('en-IN')}`} sub={`${expData.summary.total_expenses} expenses`} color="text-[#0D2850]" />
                <MetricCard icon={IndianRupee} label="Approved" value={`\u20B9${(expData.summary.approved_amount || 0).toLocaleString('en-IN')}`} sub="cleared amount" color="text-emerald-600" />
                <MetricCard icon={IndianRupee} label="Pending" value={`\u20B9${(expData.summary.pending_amount || 0).toLocaleString('en-IN')}`} sub="awaiting review" color="text-amber-600" />
                <MetricCard icon={IndianRupee} label="Rejected" value={`\u20B9${(expData.summary.rejected_amount || 0).toLocaleString('en-IN')}`} sub="declined" color="text-red-600" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Monthly Trend */}
                {expData.monthly_trend?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Monthly Expense Trend</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[260px]" data-testid="expense-trend-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={expData.monthly_trend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip {...TOOLTIP_STYLE} formatter={v => `\u20B9${Number(v).toLocaleString('en-IN')}`} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="total" name="Claimed" fill="#0D2850" radius={[4,4,0,0]} />
                            <Bar dataKey="approved" name="Approved" fill="#059669" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Category Breakdown Pie */}
                {expData.by_category?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Expense by Category</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[260px]" data-testid="expense-category-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={expData.by_category} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="amount" nameKey="category" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} style={{ fontSize: 11 }}>
                              {expData.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip {...TOOLTIP_STYLE} formatter={v => `\u20B9${Number(v).toLocaleString('en-IN')}`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Department Breakdown */}
              {expData.by_department?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Expense by Department</CardTitle></CardHeader>
                  <CardContent className="p-3">
                    <div className="h-[240px]" data-testid="expense-dept-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={expData.by_department} layout="vertical" margin={{ left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 10 }} />
                          <Tooltip {...TOOLTIP_STYLE} formatter={v => `\u20B9${Number(v).toLocaleString('en-IN')}`} />
                          <Bar dataKey="amount" name="Total Expense" fill="#7c3aed" radius={[0,4,4,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top Spenders Table */}
              {expData.top_spenders?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Top Spenders</CardTitle></CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50"><TableHead>#</TableHead><TableHead>Employee</TableHead><TableHead>Department</TableHead><TableHead className="text-center">Claims</TableHead><TableHead className="text-right">Total Amount</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {expData.top_spenders.map((s, i) => (
                          <TableRow key={s.employee_id}>
                            <TableCell className="text-sm font-medium text-slate-400">{i + 1}</TableCell>
                            <TableCell className="text-sm font-medium">{s.employee_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{s.department}</TableCell>
                            <TableCell className="text-center text-sm">{s.count}</TableCell>
                            <TableCell className="text-right text-sm font-semibold">{`\u20B9${s.total.toLocaleString('en-IN')}`}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-slate-200"><CardContent className="p-12 text-center"><p className="text-sm text-slate-400">No expense data available</p></CardContent></Card>
          )}
        </TabsContent>

        {/* Journey Plans Analytics Tab */}
        <TabsContent value="journey" className="mt-4 space-y-4" data-testid="journey-analytics-tab">
          {jrnData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <MetricCard icon={Briefcase} label="Total Plans" value={jrnData.summary.total_plans} sub="all time" color="text-[#0D2850]" />
                <MetricCard icon={CheckCircle} label="Approved" value={jrnData.summary.approved} sub={`${jrnData.summary.approval_rate}% rate`} color="text-emerald-600" />
                <MetricCard icon={Clock} label="Pending" value={jrnData.summary.pending} sub="awaiting review" color="text-amber-600" />
                <MetricCard icon={XCircle} label="Rejected" value={jrnData.summary.rejected} sub="declined" color="text-red-600" />
                <MetricCard icon={TrendingUp} label="Approval Rate" value={`${jrnData.summary.approval_rate}%`} sub="of decided plans" color="text-violet-600" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {jrnData.monthly_trend?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Monthly Submission Trend</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[260px]" data-testid="journey-trend-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={jrnData.monthly_trend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip {...TOOLTIP_STYLE} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="submitted" name="Submitted" fill="#0D2850" radius={[4,4,0,0]} />
                            <Bar dataKey="approved" name="Approved" fill="#059669" radius={[4,4,0,0]} />
                            <Bar dataKey="rejected" name="Rejected" fill="#dc2626" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {jrnData.by_department?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Plans by Department</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[260px]" data-testid="journey-dept-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={jrnData.by_department} layout="vertical" margin={{ left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                            <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 10 }} />
                            <Tooltip {...TOOLTIP_STYLE} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="approved" name="Approved" fill="#059669" radius={[0,4,4,0]} stackId="a" />
                            <Bar dataKey="pending" name="Pending" fill="#b45309" radius={[0,4,4,0]} stackId="a" />
                            <Bar dataKey="rejected" name="Rejected" fill="#dc2626" radius={[0,4,4,0]} stackId="a" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {jrnData.approval_by_dept?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Approval Rate by Department</CardTitle></CardHeader>
                  <CardContent className="p-3">
                    <div className="h-[220px]" data-testid="journey-approval-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={jrnData.approval_by_dept} layout="vertical" margin={{ left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                          <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={v => `${v}%`} {...TOOLTIP_STYLE} />
                          <Bar dataKey="rate" name="Approval Rate" fill="#7c3aed" radius={[0,4,4,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {jrnData.top_travelers?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Top Travelers</CardTitle></CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50"><TableHead>#</TableHead><TableHead>Employee</TableHead><TableHead>Department</TableHead><TableHead className="text-center">Plans</TableHead><TableHead className="text-center">Approved</TableHead><TableHead className="text-right">Rate</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {jrnData.top_travelers.map((t, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm font-medium text-slate-400">{i + 1}</TableCell>
                            <TableCell className="text-sm font-medium">{t.employee_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{t.department}</TableCell>
                            <TableCell className="text-center text-sm">{t.plans}</TableCell>
                            <TableCell className="text-center text-sm">{t.approved}</TableCell>
                            <TableCell className="text-right">
                              <Badge className={`text-[10px] ${t.plans > 0 && (t.approved / t.plans * 100) > 75 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : t.plans > 0 && (t.approved / t.plans * 100) > 50 ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}`}>
                                {t.plans > 0 ? `${Math.round(t.approved / t.plans * 100)}%` : '0%'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-slate-200"><CardContent className="p-12 text-center"><p className="text-sm text-slate-400">No journey plan data available</p></CardContent></Card>
          )}
        </TabsContent>

        {/* Insights Tab - Heatmap, Comparison, Forecasting */}
        <TabsContent value="insights" className="mt-4 space-y-4" data-testid="insights-tab">
          {/* Attendance Heatmap */}
          {heatmapData?.heatmap?.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Attendance Heatmap (Last 3 Months)</CardTitle></CardHeader>
              <CardContent className="p-4">
                <div data-testid="attendance-heatmap" className="overflow-x-auto">
                  <HeatmapGrid data={heatmapData.heatmap} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comparison Section */}
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-5 pb-1">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Month-over-Month Comparison</CardTitle>
                <div className="flex gap-1">
                  {['attendance','payroll','expenses'].map(m => (
                    <Button key={m} variant={compMetric === m ? 'default' : 'outline'} size="sm" className={`text-xs h-7 ${compMetric === m ? 'bg-[#0D2850]' : ''}`} onClick={() => switchCompMetric(m)} data-testid={`comp-${m}-btn`}>
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3">
              {comparisonData?.data?.length > 0 ? (
                <div className="h-[300px]" data-testid="comparison-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    {compMetric === 'attendance' ? (
                      <LineChart data={comparisonData.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                        <Tooltip {...TOOLTIP_STYLE} formatter={v => `${v}%`} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="rate" name="Attendance Rate" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="rate" name="Trend (SMA)" stroke="#059669" strokeWidth={1.5} strokeDasharray="6 3" dot={false} data={calcSMA(comparisonData.data, 'rate', 3)} />
                      </LineChart>
                    ) : compMetric === 'payroll' ? (
                      <BarChart data={comparisonData.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="period" tick={{ fontSize: 9 }} angle={-15} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip {...TOOLTIP_STYLE} formatter={v => INR(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="gross" name="Gross" fill="#0D2850" radius={[4,4,0,0]} />
                        <Bar dataKey="net" name="Net" fill="#059669" radius={[4,4,0,0]} />
                      </BarChart>
                    ) : (
                      <BarChart data={comparisonData.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip {...TOOLTIP_STYLE} formatter={v => INR(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="total" name="Total" fill="#7c3aed" radius={[4,4,0,0]} />
                        <Bar dataKey="approved" name="Approved" fill="#059669" radius={[4,4,0,0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">No comparison data available</p>
              )}

              {/* Change % indicators */}
              {comparisonData?.data?.length > 1 && (
                <div className="mt-3 flex gap-3 flex-wrap">
                  {comparisonData.data.slice(-3).map((d, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs">
                      <span className="text-slate-500">{d.month || d.period}:</span>
                      <Badge className={`text-[10px] ${d.change_pct > 0 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : d.change_pct < 0 ? 'bg-red-100 text-red-700 hover:bg-red-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-100'}`}>
                        {d.change_pct > 0 ? '+' : ''}{d.change_pct || 0}%
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trend Forecasting Section */}
          {data?.attendance_daily?.length > 5 && (
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Attendance Trend with Forecast</CardTitle></CardHeader>
              <CardContent className="p-3">
                <div className="h-[260px]" data-testid="forecast-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={addForecast(data.attendance_daily)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d?.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip {...TOOLTIP_STYLE} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="present" name="Present" stroke="#059669" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="forecast" name="Forecast (SMA-5)" stroke="#7c3aed" strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[10px] text-slate-400 mt-2">Forecast uses a 5-day Simple Moving Average (SMA) to project attendance trends</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Scheduled Reports Tab */}
        <TabsContent value="scheduled" className="mt-4 space-y-4" data-testid="scheduled-reports-tab">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-semibold">Monthly Scheduled Reports</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Auto-emailed on the 1st of every month to all Admin/HR users. You can also trigger manually.</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3 mb-5" data-testid="scheduled-report-actions">
                <div className="flex items-center gap-2">
                  <select id="report-month" className="h-9 rounded-md border border-slate-200 px-2 text-sm" defaultValue={new Date().getMonth()} data-testid="report-month-select">
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                      <option key={i} value={i + 1}>{m}</option>
                    ))}
                  </select>
                  <select id="report-year" className="h-9 rounded-md border border-slate-200 px-2 text-sm" defaultValue={new Date().getFullYear()} data-testid="report-year-select">
                    {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <Button
                    size="sm"
                    className="bg-[#0D2850] hover:bg-[#0B2246] h-9"
                    disabled={triggerLoading}
                    data-testid="trigger-report-btn"
                    onClick={async () => {
                      const month = parseInt(document.getElementById('report-month').value);
                      const year = parseInt(document.getElementById('report-year').value);
                      setTriggerLoading(true);
                      try {
                        const { data: res } = await api.post('/api/reports/scheduled/trigger', { month, year });
                        toast.success(res.message || 'Report sent');
                        const r = await api.get('/api/reports/scheduled');
                        setScheduledReports(r.data);
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      setTriggerLoading(false);
                    }}
                  >
                    {triggerLoading ? 'Sending...' : 'Send Report Now'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9"
                    data-testid="download-report-btn"
                    onClick={async () => {
                      const month = parseInt(document.getElementById('report-month').value);
                      const year = parseInt(document.getElementById('report-year').value);
                      try {
                        const res = await api.get(`/api/reports/scheduled/download?month=${month}&year=${year}`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a');
                        a.href = url;
                        const mNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        a.download = `Monthly_Report_${mNames[month-1]}_${year}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('PDF downloaded');
                      } catch { toast.error('Failed to download'); }
                    }}
                  >
                    <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
                  </Button>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 mb-4 flex items-start gap-3">
                <CalendarDays className="w-5 h-5 text-[#0D2850] mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#0D2850]">Automatic Schedule</p>
                  <p className="text-xs text-slate-500">Reports are automatically generated and emailed on the 1st of every month at 9:00 AM IST. The report covers the previous month's data including workforce, attendance, leave, expenses, payroll, and settlements.</p>
                </div>
              </div>

              <h4 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Report History</h4>
              {scheduledReports.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <CalendarDays className="w-7 h-7 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No scheduled reports yet</p>
                  <p className="text-xs mt-1">Reports will appear here after being generated automatically or manually.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Month</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Generated At</TableHead>
                    <TableHead>Triggered By</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {scheduledReports.map((r, i) => (
                      <TableRow key={i} data-testid={`scheduled-report-row-${i}`}>
                        <TableCell className="text-sm font-medium">{r.month}</TableCell>
                        <TableCell className="text-xs text-slate-600">{r.start_date} to {r.end_date}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${r.type === 'monthly' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                            {r.type === 'monthly' ? 'Auto' : 'Manual'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{r.recipients}</TableCell>
                        <TableCell className="text-xs text-slate-600">{r.generated_at ? new Date(r.generated_at).toLocaleString() : '-'}</TableCell>
                        <TableCell className="text-sm text-slate-500">{r.triggered_by || 'System'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-3 flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-slate-500 uppercase font-medium">{label}</p>
          <p className="text-xl font-bold text-[#0B1B33] mt-0.5">{value}</p>
          <p className="text-[10px] text-slate-400">{sub}</p>
        </div>
      </CardContent>
    </Card>
  );
}


// Simple Moving Average calculator
function calcSMA(data, key, window) {
  return data.map((d, i) => {
    if (i < window - 1) return { ...d, [key]: null };
    const slice = data.slice(i - window + 1, i + 1);
    const avg = slice.reduce((s, v) => s + (v[key] || 0), 0) / window;
    return { ...d, [key]: Math.round(avg * 10) / 10 };
  });
}

// Add forecast line using SMA-5
function addForecast(dailyData) {
  const w = 5;
  return dailyData.map((d, i) => {
    if (i < w) return { ...d, forecast: null };
    const slice = dailyData.slice(i - w, i);
    const avg = slice.reduce((s, v) => s + (v.present || 0), 0) / w;
    return { ...d, forecast: Math.round(avg) };
  });
}

// Attendance Heatmap Grid Component
function HeatmapGrid({ data }) {
  if (!data?.length) return null;

  // Group by week
  const weeks = {};
  data.forEach(d => {
    const dt = new Date(d.date);
    const weekStart = new Date(dt);
    weekStart.setDate(dt.getDate() - dt.getDay());
    const key = weekStart.toISOString().slice(0, 10);
    if (!weeks[key]) weeks[key] = [];
    weeks[key].push(d);
  });

  const getColor = (rate) => {
    if (rate >= 95) return 'bg-emerald-500';
    if (rate >= 85) return 'bg-emerald-400';
    if (rate >= 75) return 'bg-emerald-300';
    if (rate >= 60) return 'bg-amber-300';
    if (rate >= 40) return 'bg-amber-400';
    if (rate > 0) return 'bg-red-300';
    return 'bg-slate-100';
  };

  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const sortedWeeks = Object.keys(weeks).sort();

  return (
    <div>
      <div className="flex gap-0.5 mb-1 ml-6">
        {sortedWeeks.map((w, i) => {
          const month = new Date(w).toLocaleDateString('en-US', { month: 'short' });
          const prev = i > 0 ? new Date(sortedWeeks[i-1]).toLocaleDateString('en-US', { month: 'short' }) : '';
          return <div key={w} className="w-4 text-[8px] text-slate-400 text-center">{month !== prev ? month : ''}</div>;
        })}
      </div>
      {dayLabels.map((label, dayIdx) => (
        <div key={dayIdx} className="flex items-center gap-0.5 mb-0.5">
          <span className="text-[9px] text-slate-400 w-5 text-right mr-0.5">{label}</span>
          {sortedWeeks.map(wk => {
            const dayData = weeks[wk]?.find(d => new Date(d.date).getDay() === dayIdx);
            return (
              <div
                key={`${wk}-${dayIdx}`}
                className={`w-4 h-4 rounded-sm ${dayData ? getColor(dayData.rate) : 'bg-slate-50'} cursor-pointer transition-transform hover:scale-125`}
                title={dayData ? `${dayData.date}: ${dayData.rate}% (P:${dayData.present} L:${dayData.late})` : ''}
              />
            );
          })}
        </div>
      ))}
      <div className="flex items-center gap-1 mt-3 text-[9px] text-slate-500">
        <span>Less</span>
        <div className="w-3 h-3 rounded-sm bg-red-300" />
        <div className="w-3 h-3 rounded-sm bg-amber-400" />
        <div className="w-3 h-3 rounded-sm bg-amber-300" />
        <div className="w-3 h-3 rounded-sm bg-emerald-300" />
        <div className="w-3 h-3 rounded-sm bg-emerald-400" />
        <div className="w-3 h-3 rounded-sm bg-emerald-500" />
        <span>More</span>
      </div>
    </div>
  );
}
